package com.evoapps.evoshift.domain.review

import com.evoapps.evoshift.domain.model.UserPermission
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.toLocalDateTime

/** The success moments after which the app may ask for a store rating (SH-240). */
enum class SuccessMoment {
    /** A roster was published. */
    ROSTER_PUBLISHED,

    /** A day's takings were saved. */
    TAKINGS_ENTERED
}

/** Everything the decision needs; the app gathers it after the success action finished. */
data class ReviewPromptInput(
    val moment: SuccessMoment,
    val now: Instant,
    /** The person's permission at the workplace of the moment; null when they have none there. */
    val permission: UserPermission?,
    /** Their engagement's start at that workplace ("YYYY-MM-DD"); null when unknown. */
    val engagementStart: String?,
    /** The app's first start on this device (this process): never then. */
    val isFirstLaunch: Boolean,
    /** When the app last asked, on this device or another (the later of the two); null: never. */
    val lastPromptAt: Instant?,
    /** Published (or locked) rosters of that workplace, this one included. */
    val publishedRosters: Int,
    /** Days of that workplace with takings, today's included. */
    val takingsDays: Int,
    val zone: TimeZone = TimeZone.UTC
)

/** Why the app does not ask now (tests and logs); [ASK] means it does. */
enum class ReviewPromptDecision {
    ASK,
    FIRST_LAUNCH,
    NOT_A_MANAGER,
    FIRST_WEEK,
    ASKED_RECENTLY,
    TOO_EARLY
}

/**
 * When to ask for a store rating (SH-240), at a success moment: owners and managers, after their
 * workplace's 3rd published roster or its 10th day with takings, at most once per 90 days, never on
 * the app's first start, never for anyone in their first week at the workplace. Employees never
 * reach these moments (they cannot publish or enter takings) and are never asked. The store decides
 * whether its dialog really shows; the app never learns whether or what the person rated.
 */
object ReviewPromptPolicy {
    const val ROSTERS_NEEDED = 3
    const val TAKINGS_DAYS_NEEDED = 10
    val MIN_INTERVAL = 90.days
    const val FIRST_WEEK_DAYS = 7

    fun decide(input: ReviewPromptInput): ReviewPromptDecision {
        if (input.isFirstLaunch) return ReviewPromptDecision.FIRST_LAUNCH
        val permission = input.permission
        if (permission == null || permission == UserPermission.EMPLOYEE) return ReviewPromptDecision.NOT_A_MANAGER
        if (permission != UserPermission.OWNER && inFirstWeek(input)) return ReviewPromptDecision.FIRST_WEEK
        val last = input.lastPromptAt
        if (last != null && input.now - last < MIN_INTERVAL) return ReviewPromptDecision.ASKED_RECENTLY
        val reached = when (input.moment) {
            SuccessMoment.ROSTER_PUBLISHED -> input.publishedRosters >= ROSTERS_NEEDED
            SuccessMoment.TAKINGS_ENTERED -> input.takingsDays >= TAKINGS_DAYS_NEEDED
        }
        return if (reached) ReviewPromptDecision.ASK else ReviewPromptDecision.TOO_EARLY
    }

    /** Less than [FIRST_WEEK_DAYS] since the engagement started (an unknown start counts as new). */
    private fun inFirstWeek(input: ReviewPromptInput): Boolean {
        val start = input.engagementStart?.let { runCatching { LocalDate.parse(it.take(10)) }.getOrNull() } ?: return true
        val today = input.now.toLocalDateTime(input.zone).date
        return start.daysUntil(today) < FIRST_WEEK_DAYS
    }
}
